import { first } from 'lodash';
import { useEffect, useRef } from 'react';

import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { EntityDetailsAwareRoute } from '@/modules/entities/contexts/entityDetails/EntityDetailsAwareRoute';
import {
  EntityValuationActionDefinition,
  EntityValuationActionsProps,
  useEntityValuationActions,
} from '@/modules/entities/details/EntityValuation/EntityValuationActions';
import { useEntityDetailsQuery } from '@/modules/entities/details/graphql/EntityDetailPage.generated';
import { getNodes } from '@/utils/graphqlUtils';

type HoldingsEntity = EntityValuationActionsProps['entity'];

export interface EditHoldingsRequest {
  entityId: string;
  action: EntityValuationActionDefinition;
  /** Bumped per click so re-running the same action remounts and re-fires. */
  key: number;
}

interface EditHoldingsMountProps {
  entityId: string;
  action: EntityValuationActionDefinition;
  onClose: () => void;
}

/**
 * Runs a single holdings action (chosen from the entities-list row "Edit
 * holdings" menu) for one entity, reusing the entity detail page's holdings
 * behavior via `useEntityValuationActions`. The menu only prefetches a slim
 * fragment, so here we fetch the full entity detail by id (showing a loading
 * overlay) and run the action once it resolves.
 *
 * Mounted by `PresentationEntityActionsProvider` (outside the slide canvas's
 * `CustomerThemeProvider`) so the holdings modals render in the Luminary
 * app theme.
 */
export function EditHoldingsMount({
  entityId,
  action,
  onClose,
}: EditHoldingsMountProps) {
  const { data, loading } = useEntityDetailsQuery({
    variables: { entityId },
  });
  const entity = first(getNodes(data?.entities)) ?? null;

  // If the fetch settles without an entity (error or empty result), tear the
  // flow down instead of hanging with no modal and no way to recover.
  const failedToLoad = !loading && !entity;
  useEffect(() => {
    if (failedToLoad) {
      onClose();
    }
  }, [failedToLoad, onClose]);

  return (
    <EntityDetailsAwareRoute entityId={entityId}>
      {loading && <LoadingOverlay open />}
      {entity && (
        <EditHoldingsRunner entity={entity} action={action} onClose={onClose} />
      )}
    </EntityDetailsAwareRoute>
  );
}

function EditHoldingsRunner({
  entity,
  action,
  onClose,
}: {
  entity: HoldingsEntity;
  action: EntityValuationActionDefinition;
  onClose: () => void;
}) {
  const canEdit = useCanEditHouseholdData();
  const { entityId, entityNotFound } = useEntityDetailsContext();
  const { handleAction, modals, isResyncing } = useEntityValuationActions(
    entity,
    {
      isViewOnly: !canEdit,
      // Any holdings action ending — modal closed/saved, link opened — tears
      // down this mount and refetches the presentation so the slide reflects
      // changes.
      onAfterUpdate: onClose,
      onClose,
      // Keep the user in the presentation designer; don't route to the entity
      // detail page when the edit-entity modal saves.
      navigateAfterSave: false,
    }
  );

  // The shared modals read their target from the entity-details context, which
  // `EntityDetailsAwareRoute` populates from its own slim query. If that query
  // fails the context never resolves to this entity, so bail instead of
  // hanging mounted with the action never firing.
  useEffect(() => {
    if (entityNotFound) {
      onClose();
    }
  }, [entityNotFound, onClose]);

  // Fire the chosen action once the entity-details context has resolved to
  // this entity.
  const hasFiredRef = useRef(false);
  const isContextReady = entityId === entity.id;
  useEffect(() => {
    if (isContextReady && !hasFiredRef.current) {
      hasFiredRef.current = true;
      handleAction(action);
    }
  }, [isContextReady, handleAction, action]);

  return (
    <>
      {/* Resync opens no modal; keep the overlay up while it runs so the flow
          shows continuous progress rather than briefly rendering nothing. */}
      {isResyncing && <LoadingOverlay open />}
      {modals}
    </>
  );
}
